import { useNavigate } from "react-router-dom";
import { ParallaxProvider } from "react-scroll-parallax";
import { useAuth } from "../../contexts/AuthContext";
import { Users, FileText, BookOpen, Lock, ArrowRight, Sparkles, Download } from "lucide-react";
import { motion } from "framer-motion";

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94] as [number, number, number, number] }
  }
};

const Publication: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated, hasRole } = useAuth();

  // Check if user is an authenticated adherent
  const isAdherent = isAuthenticated && hasRole('adherent');

  return (
    <ParallaxProvider>
      <div className="min-h-screen bg-white selection:bg-primary/20 selection:text-primary-dark font-sans">
        {!isAdherent ? (
          // Non-adherent view - Premium membership CTA
          <>
            {/* Hero Section */}
            <section className="relative pt-32 pb-20 overflow-hidden">
              <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-primary/5 rounded-full blur-[100px] opacity-60 translate-x-1/3 -translate-y-1/3"></div>
              <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-secondary/10 rounded-full blur-[80px] opacity-40 -translate-x-1/3 translate-y-1/3"></div>

              <div className="container mx-auto px-4 relative z-10 text-center">
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.6 }}
                  className="inline-block px-4 py-1.5 rounded-full bg-primary/5 text-primary text-sm font-semibold mb-6 tracking-wide uppercase"
                >
                  Espace Publications
                </motion.div>
                <motion.h1
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8 }}
                  className="text-5xl md:text-7xl font-extrabold mb-8 tracking-tight text-gray-900"
                >
                  Publications <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-secondary">SAPI</span>
                </motion.h1>
                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.5, duration: 0.8 }}
                  className="text-xl md:text-2xl font-light text-gray-500 max-w-4xl mx-auto leading-relaxed"
                >
                  Accédez à notre bibliothèque exclusive de publications scientifiques, articles de recherche et ressources documentaires
                </motion.p>
              </div>
            </section>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
              {/* Benefits Section */}
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeInUp}
                className="mb-16"
              >
                <div className="grid md:grid-cols-3 gap-6">
                  {[
                    {
                      icon: <BookOpen className="w-8 h-8" />,
                      title: "Publications Scientifiques",
                      desc: "Accès à notre collection complète d'articles et études"
                    },
                    {
                      icon: <FileText className="w-8 h-8" />,
                      title: "Ressources Exclusives",
                      desc: "Documents et guides réservés aux membres"
                    },
                    {
                      icon: <Download className="w-8 h-8" />,
                      title: "Téléchargements",
                      desc: "Téléchargez et consultez hors ligne"
                    }
                  ].map((benefit, idx) => (
                    <motion.div
                      key={idx}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.6 + idx * 0.1 }}
                      className="bg-white p-6 rounded-2xl shadow-sm border border-primary/10 hover:shadow-xl transition-all duration-300 group"
                    >
                      <div className="w-14 h-14 bg-primary/5 rounded-xl flex items-center justify-center text-primary mb-4 group-hover:scale-110 transition-transform duration-300">
                        {benefit.icon}
                      </div>
                      <h3 className="font-semibold text-gray-900 mb-2">{benefit.title}</h3>
                      <p className="text-gray-600 text-sm leading-relaxed">{benefit.desc}</p>
                    </motion.div>
                  ))}
                </div>
              </motion.div>

              {/* Main CTA Card */}
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeInUp}
                className="max-w-4xl mx-auto"
              >
                <div className="bg-white rounded-3xl shadow-xl overflow-hidden border border-primary/10">
                  <div className="bg-gradient-to-r from-primary to-primary-dark p-8 text-white text-center">
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-white/20 rounded-2xl mb-4">
                      <Lock className="w-8 h-8 text-white" />
                    </div>
                    <h2 className="text-3xl font-bold mb-2">Contenu Réservé aux Membres</h2>
                    <p className="text-white/80">Rejoignez la SAPI pour accéder à toutes nos publications</p>
                  </div>

                  <div className="p-8 md:p-12">
                    {/* CTA Buttons */}
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                      <button
                        onClick={() => navigate('/adhesion')}
                        className="group px-8 py-4 bg-gradient-to-r from-primary to-primary-light text-white font-bold rounded-xl shadow-lg hover:shadow-xl hover:scale-[1.01] transition-all duration-200 flex items-center justify-center space-x-2"
                      >
                        <Users className="w-5 h-5" />
                        <span>Devenir Membre</span>
                        <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                      </button>

                      <button
                        onClick={() => navigate('/adherent-login')}
                        className="px-8 py-4 bg-white text-primary font-semibold rounded-xl border-2 border-primary/20 hover:border-primary hover:bg-primary/5 transition-all duration-200"
                      >
                        <span className="flex items-center justify-center">
                          <Lock className="w-5 h-5 mr-2" />
                          Se Connecter
                        </span>
                      </button>
                    </div>

                    <p className="text-center text-gray-500 text-sm mt-6">
                      Déjà membre ? <button onClick={() => navigate('/adherent-login')} className="text-primary hover:underline font-medium">Connectez-vous ici</button>
                    </p>
                  </div>
                </div>
              </motion.div>
            </div>
          </>
        ) : (
          // Adherent view - Publications content
          <>
            {/* Hero Section for Members */}
            <section className="relative pt-32 pb-20 overflow-hidden">
              <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-primary/5 rounded-full blur-[100px] opacity-60 translate-x-1/3 -translate-y-1/3"></div>
              <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-secondary/10 rounded-full blur-[80px] opacity-40 -translate-x-1/3 translate-y-1/3"></div>

              <div className="container mx-auto px-4 relative z-10 text-center">
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.6 }}
                  className="inline-block px-4 py-1.5 rounded-full bg-primary/5 text-primary text-sm font-semibold mb-6 tracking-wide uppercase"
                >
                  Espace Membre
                </motion.div>
                <motion.h1
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8 }}
                  className="text-5xl md:text-7xl font-extrabold mb-8 tracking-tight text-gray-900"
                >
                  Publications <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-secondary">SAPI</span>
                </motion.h1>
                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.5, duration: 0.8 }}
                  className="text-xl md:text-2xl font-light text-gray-500 max-w-4xl mx-auto leading-relaxed"
                >
                  Consultez notre bibliothèque de publications scientifiques et ressources documentaires
                </motion.p>
              </div>
            </section>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
              {/* Publications Content Placeholder */}
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeInUp}
                className="bg-white rounded-3xl shadow-xl p-12 text-center border border-primary/10"
              >
                <div className="inline-flex items-center justify-center w-20 h-20 bg-primary/5 rounded-2xl mb-6">
                  <BookOpen className="w-10 h-10 text-primary" />
                </div>
                <h3 className="text-3xl font-bold text-gray-900 mb-4">
                  Bibliothèque de Publications
                </h3>
                <p className="text-gray-600 mb-6 max-w-2xl mx-auto leading-relaxed">
                  Les publications seront affichées ici. Vous pouvez maintenant ajouter la liste des publications,
                  articles scientifiques et ressources documentaires.
                </p>
                <div className="inline-flex items-center space-x-2 px-4 py-2 bg-primary/5 rounded-lg border border-primary/10">
                  <Sparkles className="w-4 h-4 text-primary" />
                  <span className="text-sm text-primary font-medium">Accès Membre Actif</span>
                </div>
              </motion.div>
            </div>
          </>
        )}
      </div>
    </ParallaxProvider>
  );
};

export default Publication;
